/**
 * Daily Schedule Section
 *
 * 在 Morning Brief Modal 中顯示當天的 Schedule Timeline
 *
 * 功能：
 * - 摘要視圖：顯示統計和時間範圍
 * - Timeline 視圖：時間軸 + 項目列表
 * - 支援 TimeBox 和 Event 混合顯示
 * - 預設折疊，用戶可展開查看詳細
 */

import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ChevronRight, Calendar } from 'lucide-react';
import { TimeBox } from '../../types/timeBox';
import { Event } from '../../types/calendar-event';
import { ScheduleItem, isTimeBox, isEvent } from '../../types/scheduleItem';
import { formatDurationFromMinutes } from '../../utils/formatters';

interface DailyScheduleSectionProps {
  timeBoxes?: TimeBox[];
  events?: Event[];
}

export const DailyScheduleSection: React.FC<DailyScheduleSectionProps> = ({
  timeBoxes,
  events
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // 分離全天事件和有時間的項目
  const { allDayEvents, timedItems } = useMemo(() => {
    if (!timeBoxes && !events) return { allDayEvents: [], timedItems: [] };

    // 全天事件
    const allDay = (events || [])
      .filter(e => e.timeType === 'allday')
      .sort((a, b) => {
        // 全天事件按標題排序
        return a.title.localeCompare(b.title);
      });

    // 有時間的項目（TimeBoxes + 非全天 Events）
    const timed: ScheduleItem[] = [
      ...(timeBoxes || [])
        .filter(tb => tb.status !== 'cancelled')
        .map(tb => tb as ScheduleItem),
      ...(events || [])
        .filter(e => e.timeType !== 'allday')
        .map(e => e as ScheduleItem)
    ].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

    return { allDayEvents: allDay, timedItems: timed };
  }, [timeBoxes, events]);

  // 統計摘要
  const summary = useMemo(() => {
    const totalItems = timedItems.length + allDayEvents.length;
    if (totalItems === 0) return null;

    // 分類統計：Events (timed only), Focus, Others
    const timedEventCount = timedItems.filter(i => isEvent(i)).length;
    const focusCount = timedItems.filter(i => isTimeBox(i) && i.category === 'focus').length;
    const otherCount = timedItems.filter(i => isTimeBox(i) && i.category !== 'focus').length;

    // 組裝統計文字（省略為 0 的類別）
    const parts: string[] = [];
    if (timedEventCount > 0) parts.push(`${timedEventCount} events`);
    if (focusCount > 0) parts.push(`${focusCount} focus`);
    if (otherCount > 0) parts.push(`${otherCount} others`);

    const statsText = parts.length > 0 ? parts.join(' · ') : 'No activities';

    return {
      timedEventCount,
      focusCount,
      otherCount,
      statsText
    };
  }, [timedItems, allDayEvents]);

  // 空狀態處理
  if (timedItems.length === 0 && allDayEvents.length === 0) {
    return (
      <div className="space-y-3">
        {/* 漸變分隔線 */}
        <div className="h-px bg-gradient-to-r from-transparent via-orange-200 to-transparent" />

        {/* 極簡空狀態 */}
        <div className="text-sm text-gray-500 text-center py-2">
          No schedule today — enjoy your free time
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* 漸變分隔線 */}
      <div className="h-px bg-gradient-to-r from-transparent via-orange-200 to-transparent" />

      {/* 標題 */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-orange-700 uppercase tracking-wide">
          Today's Schedule
        </h3>
        {isExpanded && (
          <button
            onClick={() => setIsExpanded(false)}
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Collapse
          </button>
        )}
      </div>

      {/* 摘要視圖 - 極簡單行設計 */}
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-orange-50 transition-colors text-left group"
        >
          {/* 只顯示統計資訊 - 移除重複標題 + 放大文字 */}
          <div className="text-sm text-gray-800">
            {summary!.statsText}
          </div>

          {/* 展開箭頭 */}
          <ChevronRight
            size={18}
            className="text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all flex-shrink-0"
          />
        </button>
      ) : (
        /* Timeline 視圖 */
        <div className="space-y-3">
          {/* 全天事件區塊（如果有的話） */}
          {allDayEvents.length > 0 && (
            <div className="space-y-1.5 pb-3 border-b border-gray-100">
              {allDayEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center gap-2 py-1.5 px-2 rounded bg-sky-50/50 hover:bg-sky-50 transition-colors"
                >
                  {/* 全天事件圖標 */}
                  <span className="text-sm flex-shrink-0">🎯</span>

                  {/* 標題 */}
                  <span className="text-sm text-sky-900 flex-1">
                    {event.title}
                  </span>

                  {/* 全天標記 */}
                  <span className="text-xs text-sky-600 flex-shrink-0">
                    All day
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 有時間的項目 - Timeline */}
          {timedItems.length > 0 && (
            <div className="relative">
              {/* 時間軸主線 */}
              <div className="absolute left-[42px] top-2 bottom-2 w-px bg-gray-200" />

              {/* Timeline 項目 */}
              <div className="space-y-0">
                {timedItems.map((item) => {
                  const durationInMinutes = (item.endTime.getTime() - item.startTime.getTime()) / (1000 * 60);
                  const durationText = formatDurationFromMinutes(durationInMinutes);

                  // 根據類別決定圓點顏色
                  const dotColor = isTimeBox(item)
                    ? {
                        focus: 'bg-sky-500',
                        routine: 'bg-slate-400',
                        life: 'bg-green-500',
                        rest: 'bg-amber-500'
                      }[item.category]
                    : 'bg-red-500'; // Event 用紅色

                  return (
                    <div
                      key={item.id}
                      className="relative flex items-baseline gap-3 py-2.5 hover:bg-orange-50/50 -mx-1 px-1 rounded transition-colors"
                    >
                      {/* 時間 */}
                      <span className="text-xs text-gray-400 font-mono w-10 flex-shrink-0">
                        {format(item.startTime, 'HH:mm')}
                      </span>

                      {/* 時間軸節點 - 小圓點 */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-1 h-1 rounded-full ${dotColor} ring-2 ring-white`} />
                      </div>

                      {/* 標題 + Event 標記 */}
                      <div className="flex items-baseline gap-1.5 flex-1 min-w-0">
                        <span className="text-sm text-gray-900">
                          {item.title}
                        </span>
                        {isEvent(item) && (
                          <Calendar size={14} className="flex-shrink-0 text-red-500 opacity-60" />
                        )}
                      </div>

                      {/* 時長 */}
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {durationText}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
